import styled from '@emotion/styled'

export const ConversationsRoot = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 0;
  height: 100%;
  overflow: hidden;
`

export const ListPanel = styled.aside<{ width?: number }>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: ${({ width }) => width ?? 320}px;
  height: 100%;
  border-right: 1px solid rgba(9, 30, 66, 0.08);
  background: #ffffff;
  overflow: hidden;
`

export const ResizeHandle = styled.div<{ dragging?: boolean }>`
  flex-shrink: 0;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  background: transparent;
  position: relative;

  &:hover::after {
    background: #007aff;
  }

  &::after {
    content: '';
    position: absolute;
    left: 2px;
    top: 0;
    width: 2px;
    height: 100%;
    background: ${({ dragging }) => (dragging ? '#007aff' : 'transparent')};
    transition: background 0.15s;
  }
`

export const ListHeader = styled.div`
  padding: 16px 16px 0;
  background: #ffffff;
  flex-shrink: 0;
`

export const ListTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 800;
  color: #091e42;
  letter-spacing: -0.3px;
`

export const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #dfe2e6;
  border-radius: 8px;
  font-size: 13px;
  color: #091e42;
  background: #fafbfb;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;

  &::placeholder {
    color: #a6aebb;
  }

  &:focus {
    border-color: #007aff;
    background: #ffffff;
  }
`

export const FilterTabs = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(9, 30, 66, 0.06);
  overflow-x: auto;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #dfe2e6;
    border-radius: 4px;
  }
`

export const FilterTab = styled.button<{ active?: boolean }>`
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 100px;
  border: 1.5px solid ${({ active }) => (active ? '#007aff' : '#dfe2e6')};
  background: ${({ active }) => (active ? '#e6f2ff' : 'transparent')};
  color: ${({ active }) => (active ? '#007aff' : '#6b788e')};
  font-size: 12px;
  font-weight: ${({ active }) => (active ? 700 : 500)};
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    border-color: #007aff;
    color: #007aff;
  }
`

export const ListScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #dfe2e6;
    border-radius: 4px;
  }
`

export const ConversationItem = styled.button<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: ${({ selected }) => (selected ? '#e6f2ff' : 'transparent')};
  border-left: 3px solid ${({ selected }) => (selected ? '#007aff' : 'transparent')};
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;

  &:hover {
    background: ${({ selected }) => (selected ? '#e6f2ff' : '#f5f6f7')};
  }
`

export const Avatar = styled.div<{ active?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ active }) => (active ? '#007aff' : '#c2c7d0')};
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  flex-shrink: 0;
`

export const ItemBody = styled.div`
  flex: 1;
  min-width: 0;
`

export const ItemName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #091e42;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const ItemMeta = styled.div`
  font-size: 11px;
  color: #6b788e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`

export const StatusBadge = styled.span<{ status: 'bot' | 'waiting' | 'agent' | 'closed' }>`
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 100px;
  white-space: nowrap;
  flex-shrink: 0;

  ${({ status }) => {
    if (status === 'bot') return 'background: #e6f2ff; color: #007aff;'
    if (status === 'waiting') return 'background: #fff4e6; color: #ff9500;'
    if (status === 'agent') return 'background: #ebf9ee; color: #258d3f;'
    return 'background: #ebedf0; color: #6b788e;'
  }}
`

export const ChatPanel = styled.main`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fafbfb;
  overflow: hidden;
`

export const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(9, 30, 66, 0.08);
  background: #ffffff;
  flex-shrink: 0;
`

export const ChatHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`

export const ChatHeaderInfo = styled.div`
  min-width: 0;
`

export const ChatHeaderName = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: #091e42;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const ChatHeaderSub = styled.div`
  font-size: 12px;
  color: #6b788e;
  margin-top: 1px;
`

export const ChatHeaderActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
`

export const ActionButton = styled.button<{ variant?: 'danger' | 'default' | 'primary' }>`
  padding: 6px 14px;
  border-radius: 8px;
  border: none;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.12s, opacity 0.12s;

  ${({ variant }) => {
    if (variant === 'danger') return 'background: #ffebea; color: #D32F2F;'
    if (variant === 'primary') return 'background: #007aff; color: #ffffff;'
    return 'background: #ebedf0; color: #42526d;'
  }}

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

export const MessagesScroll = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #dfe2e6;
    border-radius: 4px;
  }
`

export const ScrollSentinel = styled.div`
  height: 1px;
`

export const TopLoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  padding: 6px 0 10px;
  font-size: 12px;
  color: #8993a4;
`

export const SessionDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0;
  align-self: stretch;
  font-size: 11px;
  color: #a6aebb;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(9, 30, 66, 0.08);
  }
`

export const JumpToBottomButton = styled.button`
  position: absolute;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border-radius: 100px;
  border: 1px solid rgba(9, 30, 66, 0.1);
  background: #ffffff;
  color: #091e42;
  font-size: 12.5px;
  font-weight: 600;
  box-shadow: 0 2px 10px rgba(9, 30, 66, 0.14);
  cursor: pointer;

  &:hover {
    background: #f5f6f8;
  }
`

export const BubbleRow = styled.div<{ align: 'left' | 'right' }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ align }) => (align === 'right' ? 'flex-end' : 'flex-start')};
  max-width: 72%;
  align-self: ${({ align }) => (align === 'right' ? 'flex-end' : 'flex-start')};
`

export const BubbleSenderLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #8993a4;
  margin-bottom: 2px;
  padding: 0 4px;
`

export const Bubble = styled.div<{ sender: 'client' | 'bot' | 'agent' }>`
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 13.5px;
  line-height: 1.5;
  word-break: break-word;

  ${({ sender }) => {
    if (sender === 'client')
      return 'background: #ebedf0; color: #091e42; border-bottom-left-radius: 4px;'
    if (sender === 'agent')
      return 'background: #d4f1e4; color: #0d5c36; border-bottom-right-radius: 4px;'
    // bot
    return 'background: #007aff; color: #ffffff; border-bottom-right-radius: 4px;'
  }}
`

export const BubbleTime = styled.div`
  font-size: 10px;
  color: #a6aebb;
  margin-top: 3px;
  padding: 0 4px;
`

export const InputBar = styled.div`
  display: flex;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid rgba(9, 30, 66, 0.08);
  background: #ffffff;
  flex-shrink: 0;
`

export const MessageInput = styled.textarea`
  flex: 1;
  padding: 10px 14px;
  border: 1.5px solid #dfe2e6;
  border-radius: 10px;
  font-size: 13.5px;
  font-family: inherit;
  color: #091e42;
  resize: none;
  min-height: 42px;
  max-height: 120px;
  outline: none;
  background: #fafbfb;
  transition: border-color 0.15s;

  &:focus {
    border-color: #007aff;
    background: #ffffff;
  }

  &::placeholder {
    color: #a6aebb;
  }
`

export const SendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 18px;
  border-radius: 10px;
  border: none;
  background: #007aff;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.12s, opacity 0.12s;
  align-self: flex-end;
  height: 42px;

  &:hover {
    background: #006fe8;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

export const EnterConversationBar = styled.div`
  padding: 14px 20px;
  border-top: 1px solid rgba(9, 30, 66, 0.08);
  background: #ffffff;
`

export const AttachButton = styled.button`
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 10px;
  border: 1.5px solid #dfe2e6;
  background: #fafbfb;
  color: #5c6472;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;

  &:hover {
    background: #ebedf0;
    color: #091e42;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

export const MicButton = styled(AttachButton)<{ recording?: boolean }>`
  ${({ recording }) =>
    recording &&
    `
      background: #fdecea;
      border-color: #f5b5ae;
      color: #c62828;
    `}
`

export const AttachmentPreviewBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  border-top: 1px solid rgba(9, 30, 66, 0.08);
  background: #fafbfb;
`

export const AttachmentPreviewThumb = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ebedf0;
  color: #5c6472;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

export const AttachmentPreviewInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const AttachmentPreviewName = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  color: #091e42;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const AttachmentCaptionInput = styled.input`
  border: none;
  background: transparent;
  font-size: 12.5px;
  color: #5c6472;
  outline: none;
  padding: 0;

  &::placeholder {
    color: #a6aebb;
  }
`

export const AttachmentPreviewActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`

export const AttachmentError = styled.div`
  padding: 6px 20px 0;
  font-size: 12px;
  color: #c62828;
  background: #fafbfb;
`

export const RecordingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  padding: 0 14px;
  font-size: 13px;
  color: #c62828;
  font-weight: 600;

  span.dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #c62828;
    animation: chat-recording-pulse 1s ease-in-out infinite;
  }

  @keyframes chat-recording-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`

export const MediaBubbleImage = styled.img`
  display: block;
  max-width: 240px;
  max-height: 240px;
  border-radius: 10px;
  cursor: pointer;
  object-fit: cover;
`

export const MediaBubbleVideo = styled.video`
  display: block;
  max-width: 260px;
  max-height: 280px;
  border-radius: 10px;
`

export const MediaBubbleAudio = styled.audio`
  display: block;
  width: 240px;
`

export const MediaBubbleDocument = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.6);
  color: inherit;
  text-decoration: none;
  min-width: 200px;

  &:hover {
    background: rgba(255, 255, 255, 0.85);
  }
`

export const MediaBubbleDocumentInfo = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`

export const MediaBubbleDocumentName = styled.div`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
`

export const MediaBubbleDocumentHint = styled.div`
  font-size: 11px;
  opacity: 0.75;
`

export const MediaCaption = styled.div`
  margin-top: 6px;
  font-size: 13px;
`

export const LightboxImage = styled.img`
  display: block;
  max-width: 90vw;
  max-height: 85vh;
  margin: 0 auto;
`

export const InfoPanel = styled.aside<{ width?: number }>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: ${({ width }) => width ?? 280}px;
  height: 100%;
  border-left: 1px solid rgba(9, 30, 66, 0.08);
  background: #ffffff;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #dfe2e6;
    border-radius: 4px;
  }
`

export const InfoSection = styled.div`
  padding: 16px;
  border-bottom: 1px solid rgba(9, 30, 66, 0.06);
`

export const InfoSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #8993a4;
  margin-bottom: 10px;
`

export const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const InfoLabel = styled.div`
  font-size: 11px;
  color: #8993a4;
  font-weight: 600;
`

export const InfoValue = styled.div`
  font-size: 13px;
  color: #091e42;
  font-weight: 500;
  word-break: break-all;
`

export const ContextTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`

export const ContextTr = styled.tr`
  &:nth-of-type(odd) {
    background: #fafbfb;
  }
`

export const ContextTd = styled.td`
  padding: 5px 6px;
  vertical-align: top;
  color: #091e42;

  &:first-of-type {
    font-weight: 600;
    color: #42526d;
    white-space: nowrap;
    padding-right: 10px;
  }
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #8993a4;
  font-size: 14px;
  text-align: center;
  padding: 40px;
  gap: 8px;
`

export const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #8993a4;
  font-size: 13px;
`

export const CallBanner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 20px;
  background: #258d3f;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`

export const CallBannerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
  font-weight: 600;
  min-width: 0;
`

export const CallBannerActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`

export const CallActionButton = styled.button<{ variant: 'answer' | 'reject' | 'mute' | 'hangup' }>`
  padding: 6px 16px;
  border-radius: 100px;
  border: none;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.12s;
  white-space: nowrap;

  ${({ variant }) => {
    if (variant === 'answer') return 'background: #ffffff; color: #258d3f;'
    if (variant === 'reject') return 'background: rgba(255,255,255,0.15); color: #ffffff;'
    if (variant === 'hangup') return 'background: #ffebea; color: #D32F2F;'
    return 'background: #ebedf0; color: #42526d;'
  }}

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const CallCountPill = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.2);
  white-space: nowrap;
`

export const CallDock = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 200;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  min-width: 260px;
`

export const CallWaitingCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid #ff9500;
  background: #fff4e6;
  color: #42526d;
  font-size: 12.5px;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);

  span.dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ff9500;
    flex-shrink: 0;
    animation: call-waiting-pulse 1s ease-in-out infinite;
  }

  span.name {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span.count {
    margin-left: auto;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 100px;
    background: #ff9500;
    color: #ffffff;
  }

  @keyframes call-waiting-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`

export const InCallBar = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 18px;
  border-radius: 14px;
  background: #091e42;
  color: #ffffff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
`

export const InCallInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

export const InCallName = styled.div`
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const InCallStatus = styled.div`
  font-size: 11.5px;
  color: #a6aebb;
`

export const InCallActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
`
